<template>
  <Modal
    v-model:visible="isVisible"
    class="print-bol-template-modal"
    size="sm"
    :header="t('shipments.printBol')"
    @keyup.esc="onCancel"
  >
    <InputWrapper
      v-slot="{ ariaProps, inputId }"
      input-id="print-bol-template"
      :label="t('templates.templates', 1)"
    >
      <RygenDropdown
        :id="inputId"
        v-model="selected"
        v-bind="ariaProps"
        class="w-full"
        append-to="body"
        option-label="label"
        option-value="value"
        :options="dropdownOptions"
        :placeholder="t('templates.templates', 1)"
      />
    </InputWrapper>

    <template #footer>
      <RygenButton :label="t('shipments.printBol')" @click="onPrint" />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, PropType, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { DocumentTemplate } from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';

const defaultValue = -1;

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  templates: {
    type: Array as PropType<DocumentTemplate[]>,
    default: () => [],
  },
  matchedTemplateId: {
    type: Number,
    default: undefined,
  },
});
const { templates, matchedTemplateId } = toRefs(props);

const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'confirm', templateId?: number): void;
}>();

const { t } = useI18n();

const selected = ref(defaultValue);

const dropdownOptions = computed(() => {
  const defaultOption = { label: t('common.default'), value: defaultValue };

  const templateOptions = templates.value.map((template) => ({
    label: template.name,
    value: template.id,
  }));

  return [defaultOption, ...templateOptions];
});

const onCancel = async () => {
  emit('cancel');
  isVisible.value = false;
};

const onPrint = async () => {
  emit('confirm', selected.value === defaultValue ? undefined : selected.value);
  isVisible.value = false;
};

watch(isVisible, (visible) => {
  if (visible) {
    selected.value = matchedTemplateId?.value ?? defaultValue;
  }
});
</script>
